import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FileDiff, Plus, Calendar, DollarSign, ArrowRight, Eye, Trash2 } from 'lucide-react';
import PrintScheduleButton from '@/components/PrintScheduleButton';
import usePermissions from '@/hooks/usePermissions';

export default function VariationOrdersTab({
  projectId,
  variationOrders,
  selectedVO,
  setSelectedVO,
  handleDeleteVO,
  project
}) {
  const navigate = useNavigate();
  const permissions = usePermissions();

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <Button
        onClick={() => navigate('/')}
        variant="ghost"
        className="text-slate-600 hover:text-slate-900"
        data-testid="vos-back-button"
      >
        ← Back to Dashboard
      </Button>
      
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-barlow font-semibold uppercase">Variation Orders</h2>
          <p className="text-sm text-slate-500 mt-1">{variationOrders.length} variation orders</p>
        </div>
        <div className="flex gap-2">
          <PrintScheduleButton moduleType="VariationOrder" data={variationOrders} project={project} />
          <Button 
            onClick={() => navigate(`/projects/${projectId}/vos?action=new`)}
            className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm"
          >
            <Plus className="h-4 w-4 mr-2" />
            New VO
          </Button>
        </div>
      </div>
      
      {variationOrders.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <FileDiff className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No variation orders yet</p>
            <Button 
              variant="outline" 
              className="mt-4"
              onClick={() => navigate(`/projects/${projectId}/vos?action=new`)}
            >
              Create First VO
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {variationOrders.map(vo => (
            <Card 
              key={vo.id} 
              className={`cursor-pointer hover:border-slate-400 transition-colors ${selectedVO?.id === vo.id ? 'border-slate-900 ring-1 ring-slate-900' : ''}`}
              onClick={() => setSelectedVO(selectedVO?.id === vo.id ? null : vo)}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-mono font-bold">{vo.vo_number}</span>
                      <Badge className={vo.status === 'VO Issued' ? 'bg-green-100 text-green-700' : vo.status === 'Draft' ? 'bg-slate-100 text-slate-700' : 'bg-amber-100 text-amber-700'}>
                        {vo.status}
                      </Badge>
                      {vo.priority === 'Urgent' && <Badge className="bg-red-100 text-red-700">Urgent</Badge>}
                    </div>
                    <p className="text-sm text-slate-700">{vo.description}</p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                      <span><Calendar className="h-3 w-3 inline mr-1" />{vo.date_raised ? new Date(vo.date_raised).toLocaleDateString() : '-'}</span>
                      <span><DollarSign className="h-3 w-3 inline mr-1" />{vo.total_value ? `R ${vo.total_value.toLocaleString()}` : '-'}</span>
                    </div>
                  </div>
                  <ArrowRight className={`h-5 w-5 text-slate-400 transition-transform ${selectedVO?.id === vo.id ? 'rotate-90' : ''}`} />
                </div>
                
                {/* Expanded Detail */}
                {selectedVO?.id === vo.id && (
                  <div className="mt-4 pt-4 border-t space-y-3">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-slate-500">Contractor Quote</p>
                        <p className="font-medium">{vo.contractor_quote ? `R ${vo.contractor_quote.toLocaleString()}` : '-'}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Approved Value</p>
                        <p className="font-medium">{vo.approved_value ? `R ${vo.approved_value.toLocaleString()}` : '-'}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Days Impact</p>
                        <p className="font-medium">{vo.time_impact_days || '-'} days</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Subcontractor</p>
                        <p className="font-medium">{vo.subcontractor_name || '-'}</p>
                      </div>
                    </div>
                    {vo.justification && (
                      <div>
                        <p className="text-slate-500 text-sm">Justification</p>
                        <p className="text-sm">{vo.justification}</p>
                      </div>
                    )}
                    <div className="flex gap-2 mt-4">
                      <Button size="sm" onClick={(e) => { e.stopPropagation(); navigate(`/projects/${projectId}/vos/${vo.id}`); }}>
                        <Eye className="h-4 w-4 mr-1" />
                        View Full Details
                      </Button>
                      {permissions.can('variation_orders', 'delete') && (
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={(e) => handleDeleteVO(vo.id, vo.vo_number, e)}
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        Delete
                      </Button>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
